import React, { useEffect, useState } from 'react';
import { 
  AnalyticalTable, 
  Button, 
  ObjectStatus, 
  BusyIndicator,
  FlexBox,
  FlexBoxJustifyContent,
  Input,
  Select,
  Option,
  Text,
  Title
} from '@ui5/webcomponents-react';
import axiosInstance from '../../utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { formatNumberWithComma, rightAlignedNumberStyle, getStatusStyle } from '../../utils/validator';
import usePermission from '../../hooks/usePermission';
import useAutoTranslate from '../../hooks/useAutoTranslate';

const RampList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useState({
    name: '',
    status: '',
    asnNumber: ''
  });
  const [tempSearchParams, setTempSearchParams] = useState({
    name: '',
    status: '',
    asnNumber: ''
  });
  const [shouldSearch, setShouldSearch] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    if (shouldSearch) {
      const debounceTimeout = setTimeout(() => {
        setSearchParams(tempSearchParams);
        setCurrentPage(1);
        setShouldSearch(false);
      }, 500); // Debounce de 500ms para evitar demasiadas peticiones
      
      return () => clearTimeout(debounceTimeout);
    }
  }, [shouldSearch, tempSearchParams]);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, searchParams]);

  const handleSearchInputChange = (field, value) => {
    setTempSearchParams(prev => ({
      ...prev,
      [field]: typeof value === 'string' ? value.trim() : value
    }));
    setShouldSearch(true);
  };

  const handleStatusChange = (event) => {
    setTempSearchParams(prev => ({
      ...prev,
      status: event.detail.selectedOption.value
    }));
    setShouldSearch(true);
  };

  const handleResetFilters = () => {
    // Restablecer todos los filtros a su estado inicial
    setTempSearchParams({
      name: '',
      status: '',
      asnNumber: ''
    });
    
    // Actualizar inmediatamente los parámetros de búsqueda
    setSearchParams({
      name: '',
      status: '',
      asnNumber: ''
    });
    
    // Volver a la primera página y cargar los datos sin filtros
    setCurrentPage(1);
    fetchData(1);
  };

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page,
        size: 10,
        ...(searchParams.name && { name: searchParams.name }),
        ...(searchParams.asnNumber && { asnNumber: searchParams.asnNumber.trim() }),
        ...(searchParams.status && { status: searchParams.status })
      });

      const url = `virtual-ramps/search?${queryParams.toString()}`;
      const response = await axiosInstance.get(url);
      console.log("search",queryParams, response.data);

      const responseData = Array.isArray(response.data) ? response.data : 
                           response.data?.records?.data || 
                           response.data?.data || 
                           [];

      // Count assignments for each ramp based on assignmentIds
      const updatedData = await Promise.all(responseData.map(async ramp => {
          const assignmentIds = ramp.assignmentIds || [];
          const assignments = await Promise.all(assignmentIds.map(async (id) => {
              const assignmentResponse = await axiosInstance.get(`virtual-ramp-assignments/${id}`);
              return assignmentResponse.data.data || assignmentResponse.data;
          }));

          // Calculate total number of items (notifications) from all assignments
          const totalItemCount = assignments.reduce((sum, assignment) => {
              return sum + (assignment.assignmentItemIds?.length || 0);
          }, 0);

          // Extract ASN Document IDs from item details (if still needed for other calculations)
          const itemIds = assignments.flatMap(assignment => assignment.assignmentItemIds || []);
          const itemDetails = await Promise.all(itemIds.map(async (itemId) => {
              const itemResponse = await axiosInstance.get(`virtual-ramp-assignments/items/${itemId}`);
              return itemResponse.data.data || itemResponse.data;
          }));

          // Extract ASN Document IDs from item details
          const asnDocumentIds = itemDetails.map(item => item.asnDocumentId).filter(Boolean);

          // Fetch ASN documents for each ASN Document ID
          const asnDocuments = await Promise.all(asnDocumentIds.map(async (asnId) => {
              const asnResponse = await axiosInstance.get(`/asn-documents/${asnId}`);
              console.log("asndata",asnResponse.data.data)
              return asnResponse.data.data || asnResponse.data;
          }));

          // Calculate total weight and volume from ASN documents
          const totalWeight = asnDocuments.reduce((sum, asn) => sum + (asn.totalWeight || 0), 0);
          const totalVolume = asnDocuments.reduce((sum, asn) => sum + (asn.totalVolume || 0), 0);

          return {
              ...ramp,
              itemCount: totalItemCount, // Use the new totalItemCount
              assignments,
              itemDetails,
              asnDocuments,
              totalWeight,
              totalVolume
          };
      }));

      setData(updatedData);
    } catch (err) {
      setError(autoT(`Error: ${err.message}`));
      console.error(autoT("Error fetching data:"), err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (row) => {
    navigate(`/ramp/${row.id}`);
  };

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const statusOptions = [
    { value: '', label: autoT('Todos') },
    { value: 'AVAILABLE', label: autoT('Disponible') },
    { value: 'OCCUPIED', label: autoT('Ocupado') },
    { value: 'FINALIZED', label: autoT('Finalizado') },
  ];

  const getStatusLabel = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  const columns = [
    { 
      Header: autoT('Código Rampla'), 
      accessor: 'rampCode',
      sortable: true,
      hAlign: 'center'
    },
    { 
      Header: autoT('Nombre Rampla'), 
      accessor: 'name',
      sortable: true,
      hAlign: 'center'
    },
    {
        Header: autoT('Estado Rampla'),
        accessor: 'status',
        sortable: true,
        hAlign: 'center',
        Cell: ({ row }) => {
          const status = row.original.status;
          const style = getStatusStyle(status);
  
          return (
            <ObjectStatus
              style={{
                marginLeft: '15px',
                width: '120px',
                borderRadius: '4px',
                padding: '5px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                ...style,
              }}
            >
              {getStatusLabel(status)}
            </ObjectStatus>
          );
        },
      },
      { 
        Header: autoT('N° Not.'),
        accessor: 'itemCount',
        sortable: true,
        hAlign: 'center',
        width: 80,
        Cell: ({ row }) => (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 28px'
          }}>
            {row.original.itemCount}
          </div>
        )
      },
      { 
        Header: autoT('Peso Actual'), 
        accessor: 'capacityM',
        sortable: true,
        hAlign: 'center',
        Cell: ({ row }) => {
          const totalWeight = row.original.totalWeight || 0;
          const maxWeight = row.original.maxWeightCapacityTons || 0;
          const weightPercentage = (totalWeight / maxWeight) * 100;
          
          let textColor = 'inherit';
          if (weightPercentage >= 90) {
            textColor = weightPercentage > 100 ? '#dc3545' : '#ffc107'; // red if > 100%, yellow if >= 90%
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
              <span style={{ ...rightAlignedNumberStyle(), color: textColor }}>
                {formatNumberWithComma(row.original.totalWeight)} kg
              </span>
            </div>
          );
        }
      },
      { 
        Header: autoT('Peso Máximo'), 
        accessor: 'capacity',
        sortable: true,
        hAlign: 'center',
        Cell: ({ row }) => (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
            <span style={rightAlignedNumberStyle()}>
              {formatNumberWithComma(row.original.maxWeightCapacityTons)} kg
            </span>
          </div>
        )
      },
      { 
        Header: autoT('Volumen Actual (m³)'), 
        accessor: 'capacityV',
        sortable: true,
        hAlign: 'center',
        Cell: ({ row }) => {
          const totalVolume = row.original.totalVolume || 0;
          const maxVolume = row.original.maxVolumeCapacityCubicMeters || 0;
          const volumePercentage = (totalVolume / maxVolume) * 100;
          
          let textColor = 'inherit';
          if (volumePercentage >= 90) {
            textColor = volumePercentage > 100 ? '#dc3545' : '#ffc107'; // red if > 100%, yellow if >= 90%
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
              <span style={{ ...rightAlignedNumberStyle(), color: textColor }}>
                {formatNumberWithComma(row.original.totalVolume)} m³
              </span>
            </div>
          );
        }
      },
      { 
        Header: autoT('Volumen Máximo'), 
        accessor: 'capacityVM',
        sortable: true,
        hAlign: 'center',
        Cell: ({ row }) => (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
            <span style={rightAlignedNumberStyle()}>
              {formatNumberWithComma(row.original.maxVolumeCapacityCubicMeters)} m³
            </span>
          </div>
        )
      },
    { 
      Header: autoT('Código Puerto'), 
      accessor: 'physicalDockNumber',
      sortable: true,
      hAlign: 'center'
    }
      // },
      // { 
      //   Header: autoT('Bodega'), 
      //   accessor: 'warehouseId',
      //   sortable: true,
      //   hAlign: 'center'
      // }


  ];

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <BusyIndicator active="true" size="L" text={autoT('Cargando ramplas...')} />
    </div>
  );

  if (error) return <div style={{ padding: '20px', color: 'red' }}>{autoT(error)}</div>;

  return (
    
    
    <div style={{ width: '100%', overflow: 'visible', position: 'relative', paddingBottom: '80px' }}>    
        <FlexBox justifyContent={FlexBoxJustifyContent.End} style={{ marginBottom: '1rem' }}>
        {hasPermission('virtualramp', 'create') && (
          <Button 
            design="Emphasized"
            onClick={() => navigate('/create-ramp')}
            icon="add"
          >
            {autoT('Nueva Rampla')}
          </Button>
        )}
      </FlexBox>  
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '15px',
        marginBottom: '20px'
      }}>
        <Title level="H4" style={{ marginBottom: '1rem' }}>{autoT('Búsqueda avanzada')}</Title>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Nombre Rampla')}
            </label>
            <Input 
              value={tempSearchParams.name}
              onChange={(e) => handleSearchInputChange('name', e.target.value)}
              placeholder={autoT('Ingrese nombre')}
              style={{ width: '100%' }}
            />
          </div>
          
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Estado')}
            </label>
            <Select
              style={{ width: '100%' }}
              onChange={handleStatusChange}
              value={tempSearchParams.status}
            >
              {statusOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Número Notificación')}
            </label>
            <Input 
              value={tempSearchParams.asnNumber}
              onChange={(e) => handleSearchInputChange('asnNumber', e.target.value)}
              placeholder={autoT('Ingrese número Notificación')}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            design="Transparent" 
            onClick={handleResetFilters}
          >
            {autoT('Limpiar filtros')}
          </Button>
        </div>
      </div>

      {/* Tabla de rampas */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <AnalyticalTable
          data={data}
          columns={columns}
          minRows={5}
          visibleRows={Math.min(data.length, 10)}
          style={{
            flex:1,
            width: '100%',
            '--sapList_HeaderBackground': '#f5f6f7',
            '--sapList_BorderColor': '#e5e5e5',
          }}
          onRowClick={(event) => handleRowClick(event.detail.row.original)}
          sortable={true}
          header
          alternateRowColor
          rowHeight={44}
        />
      </div>
      <div style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
        borderTop: '1px solid #e5e5e5',
        zIndex: 100,
        width: '100%'
      }}>
        <Button onClick={handlePreviousPage} disabled={currentPage === 1}>← {autoT('Anterior')}</Button>
        <span style={{ flex: 1, textAlign: 'center' }}>{autoT('Página')} {currentPage}</span>
        <Button onClick={handleNextPage} style={{ marginRight: '2rem' }}>{autoT('Siguiente')} →</Button>
      </div>
    </div>
  );
};

export default RampList;